import { X } from 'lucide-react';
import { type JSX, type ReactNode, useEffect, useId, useState } from 'react';

import { cn } from '../lib/cn';
import { useDismissableOverlay } from '../lib/use-dismissable-overlay';
import { usePrefersReducedMotion } from '../lib/use-prefers-reduced-motion';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  className?: string;
}

/**
 * A hand-rolled dialog (WAI-ARIA APG pattern): `role="dialog"`,
 * `aria-modal="true"`, real focus trap via `useDismissableOverlay`, closes
 * on Escape or a backdrop click, and returns focus to the trigger.
 *
 * Mounts conditionally on `open` (no `createPortal`) \u2014 same strategy the
 * eight hand-rolled overlays in `apps/web-admin` already use.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  className,
}: ModalProps): JSX.Element | null {
  const titleId = useId();
  const { containerRef } = useDismissableOverlay({ open, onClose });
  const prefersReducedMotion = usePrefersReducedMotion();

  // Reduced motion: the panel mounts already in its final state, nothing to
  // transition into. Otherwise it mounts hidden and flips to visible on the
  // next frame, so the browser has an initial state to transition from.
  const [entered, setEntered] = useState(prefersReducedMotion);

  useEffect(() => {
    if (!open || prefersReducedMotion) return;

    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open, prefersReducedMotion]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl transition-all duration-200 ease-out focus:outline-none',
          entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1 scale-95 opacity-0',
          className,
        )}
        style={{ maxHeight: '90vh' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>

        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
